import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { StatusProvider } from './context/StatusContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import Work from './pages/Work';
import Contact from './pages/Contact';
import Cloud from './pages/Cloud';
import StockBot from './pages/StockBot';
import InvestorOutreach from './pages/InvestorOutreach';
import FreelanceAudit from './pages/FreelanceAudit';
import PartnershipApplication from './pages/PartnershipApplication';
import PositionApplication from './pages/PositionApplication';
import SubmitTicket from './pages/SubmitTicket';
import Outage from './pages/Outage';
import Status from './pages/Status';
import Tos from './pages/Tos';
import Privacy from './pages/Privacy';
import ServiceGuard from './components/ServiceGuard';
import { RedirectToHosting } from './components/RedirectToHosting';
import { ScrollBar, NoiseOverlay } from './components/ui';
import { recordHostingDocumentVisit } from './api/client';
import { VISIT_KEY_FAIRCORP_HUB } from './constants/visitKeys';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

const App: React.FC = () => {
  const location = useLocation();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  useEffect(() => {
    recordHostingDocumentVisit(VISIT_KEY_FAIRCORP_HUB);
  }, []);

  // New page = start at the top. A hash scrolls to that section.
  useEffect(() => {
    if (location.hash) {
      const id = decodeURIComponent(location.hash.slice(1));
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ block: 'start' });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);

  return (
    <StatusProvider>
    <div
      className="flex flex-col min-h-screen min-h-[100dvh] bg-secondary-950 text-white overflow-x-clip"
      style={{ fontFamily: "'DM Sans','Inter',system-ui,sans-serif" }}
    >
      <ScrollBar />
      <NoiseOverlay />
      <Navbar />
      <div
        className="shrink-0"
        style={{ height: 'var(--header-height, 3.5rem)' }}
        aria-hidden="true"
      />
      <main className="flex-1">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={reduceMotion ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduceMotion ? undefined : { opacity: 0 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
          >
            <Routes location={location}>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/services" element={<Services />} />
              <Route path="/work" element={<Work />} />
              <Route path="/staff" element={<Navigate to="/about#team" replace />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/cloud" element={<ServiceGuard><Cloud /></ServiceGuard>} />
              <Route path="/stockbot" element={<ServiceGuard><StockBot /></ServiceGuard>} />
              <Route path="/investor" element={<ServiceGuard><InvestorOutreach /></ServiceGuard>} />
              <Route path="/taxi" element={<Navigate to="/services" replace />} />
              <Route path="/audit" element={<ServiceGuard><FreelanceAudit /></ServiceGuard>} />
              <Route path="/apply/partnership" element={<PartnershipApplication />} />
              <Route path="/apply/position" element={<PositionApplication />} />
              <Route path="/ticket" element={<SubmitTicket />} />
              <Route path="/outage" element={<Outage />} />
              <Route path="/status" element={<Status />} />
              <Route path="/tos" element={<Tos />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/hosting" element={<RedirectToHosting />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </main>
      <Footer />
    </div>
    </StatusProvider>
  );
};

export default App;
